<script setup lang="ts">
import { formatDate, formatMoney, type LocalDate, type Money } from '@taler/domain'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import MoneyLineChart from '@/app/components/MoneyLineChart.vue'

interface ProjectionPoint {
  readonly date: LocalDate
  readonly netWorth: Money
}

const props = defineProps<{ points: readonly ProjectionPoint[]; isFlat: boolean }>()
const { t, locale } = useI18n()
const localeCode = computed(() => locale.value as 'en' | 'hu')

const points = computed(() => props.points.map((point) => ({ date: point.date, value: point.netWorth })))
const first = computed(() => props.points[0])
const last = computed(() => props.points.at(-1))

const summaryLabel = computed(() => {
  if (first.value === undefined || last.value === undefined) return ''
  return t('forecast.projectionChartLabel', {
    from: formatDate(first.value.date, localeCode.value),
    to: formatDate(last.value.date, localeCode.value),
    fromValue: formatMoney(first.value.netWorth, { locale: localeCode.value }),
    toValue: formatMoney(last.value.netWorth, { locale: localeCode.value }),
  })
})
</script>

<template>
  <div v-if="first && last" class="flex flex-col gap-2">
    <p v-if="isFlat" class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">
      {{ t('forecast.flatProjectionNote') }}
    </p>

    <MoneyLineChart :points="points" :label="summaryLabel" :locale="localeCode" area />
  </div>
</template>
